/**
 * Gate 1 Vector Store surface form helpers.
 * Provider-aware: one form contract, field schema driven by provider.
 * Distinct from dbSurfaceForm — retrieval operation config, not SQL connectivity.
 */

export const VECTOR_PROVIDER = "pgvector";

export const VECTOR_STORE_PROVIDER_OPTIONS = [
  { value: "pgvector", label: "PostgreSQL + pgvector" },
  { value: "pinecone", label: "Pinecone" },
  { value: "mongodb", label: "MongoDB Atlas Vector Search" },
  { value: "weaviate", label: "Weaviate", unavailable: true },
];

export const VECTOR_STORE_PROVIDERS = VECTOR_STORE_PROVIDER_OPTIONS.map(
  (option) => option.value
);

/** UI-only: Weaviate stays in the list but cannot be selected yet. */
export function isVectorStoreProviderUnavailable(provider) {
  const normalized = normalizeVectorStoreProvider(provider);
  return VECTOR_STORE_PROVIDER_OPTIONS.some(
    (option) => option.value === normalized && option.unavailable
  );
}

export function vectorStoreProviderMenuLabel(option) {
  if (!option) return "";
  return option.unavailable ? `${option.label} (coming soon)` : option.label;
}

const SQL_IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const EMPTY_VECTOR_STORE_FORM = {
  provider: "",
  host: "",
  port: "",
  database: "",
  username: "",
  table: "documents",
  indexName: "",
  namespace: "",
  recordId: "",
  collection: "",
  vectorPath: "",
  url: "",
};

export const EMPTY_VECTOR_STORE_CREDENTIALS = {
  password: "",
  apiKey: "",
  connectionString: "",
};

/** Pre-filled toy search values — filling only these does not mean the section is in use. */
const VECTOR_TOY_DEFAULT_KEYS = [
  "table",
  "vectorColumn",
  "dimension",
  "queryVector",
  "topK",
  "numCandidates",
];

function filled(value) {
  return Boolean(String(value ?? "").trim());
}

/** True when the user has chosen a provider, entered credentials, or filled a real connection field. */
export function isVectorStoreInUse(form = {}, credentials = {}) {
  if (filled(form.provider)) return true;
  if (
    filled(credentials.password) ||
    filled(credentials.apiKey) ||
    filled(credentials.connectionString)
  ) {
    return true;
  }
  return Object.keys(EMPTY_VECTOR_STORE_FORM).some((key) => {
    if (key === "provider" || VECTOR_TOY_DEFAULT_KEYS.includes(key)) return false;
    return filled(form[key]);
  });
}

/** Shared search fields + provider-specific connection fields. */
export const VECTOR_STORE_PROVIDER_FIELDS = {
  pgvector: [
    { key: "host", label: "Host", required: true, placeholder: "127.0.0.1" },
    { key: "port", label: "Port", required: true, placeholder: "5432", kind: "port" },
    { key: "database", label: "Database", required: true, placeholder: "rag_poc" },
    { key: "username", label: "Username", required: true },
    {
      key: "password",
      label: "Password",
      required: true,
      credential: true,
      placeholder: "Password",
    },
    { key: "table", label: "Table", required: true },
  ],
  pinecone: [
    {
      key: "apiKey",
      label: "API key",
      required: true,
      credential: true,
      placeholder: "API key",
    },
    { key: "indexName", label: "Index name", required: true, placeholder: "my-index" },
    { key: "namespace", label: "Namespace", required: false, placeholder: "optional" },
  ],
  mongodb: [
    {
      key: "connectionString",
      label: "Connection string",
      required: true,
      credential: true,
      placeholder: "mongodb+srv://…",
      fullWidth: true,
    },
    { key: "database", label: "Database", required: true, placeholder: "rag" },
    { key: "collection", label: "Collection", required: true, placeholder: "documents" },
    { key: "indexName", label: "Index name", required: true, placeholder: "vector_index" },
    { key: "vectorPath", label: "Vector path", required: true, placeholder: "embedding" },
  ],
  weaviate: [
    {
      key: "url",
      label: "URL / Endpoint",
      required: true,
      placeholder: "https://example.weaviate.network",
      fullWidth: true,
    },
    {
      key: "apiKey",
      label: "API key",
      required: true,
      credential: true,
      placeholder: "Not stored — used for this test only",
    },
    { key: "collection", label: "Collection", required: true, placeholder: "Documents" },
  ],
};

export function normalizeVectorStoreProvider(value) {
  const t = String(value || "").trim().toLowerCase();
  if (!t) return "";
  if (t === "pgvector" || t.includes("pgvector")) return "pgvector";
  if (t === "pinecone") return "pinecone";
  if (t === "mongodb" || t === "mongo" || t.includes("mongodb")) return "mongodb";
  if (t === "weaviate") return "weaviate";
  return "";
}

export function fieldsForProvider(provider) {
  const normalized = normalizeVectorStoreProvider(provider);
  return VECTOR_STORE_PROVIDER_FIELDS[normalized] || [];
}

/** Digits only; drop letters/spaces/symbols/decimals; cap at 65535. */
export function sanitizePortInput(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (Number(digits) <= 65535) return digits;
  let next = digits;
  while (next.length > 0 && Number(next) > 65535) {
    next = next.slice(0, -1);
  }
  return next;
}

export function parseQueryVectorInput(raw) {
  if (Array.isArray(raw)) return { ok: true, value: raw };
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return { ok: false, error: "Query vector is required." };
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) {
      return { ok: false, error: "Query vector must be a JSON array of numbers." };
    }
    return { ok: true, value: parsed };
  } catch {
    return { ok: false, error: "Query vector must be valid JSON (e.g. [0.9, 0.1, 0])." };
  }
}

function withOperationDefaults(form) {
  return {
    ...EMPTY_VECTOR_STORE_FORM,
    ...form,
    table: form.table || "documents",
  };
}

/**
 * Prefill non-secret values when discovery names a vector store.
 * Never invents a provider, host, index, or credentials.
 * A provider the user already saved or selected is kept.
 */
export function vectorStoreFormFromDiscovery(discovery, existing = EMPTY_VECTOR_STORE_FORM) {
  const methods = Array.isArray(discovery?.accessMethods) ? discovery.accessMethods : [];
  const discoveredProvider = normalizeVectorStoreProvider(discovery?.providers?.vectorStore);
  const existingProvider = normalizeVectorStoreProvider(existing.provider);
  const provider = existingProvider || discoveredProvider;
  const base = withOperationDefaults({
    ...EMPTY_VECTOR_STORE_FORM,
    ...existing,
    provider,
  });

  if (!methods.includes("vector-store") && !discoveredProvider) {
    return base;
  }

  return base;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

/** Hydrate saved vector-store secrets from gate0_data.vector_store. */
export function vectorCredentialsFromConfig(config = {}) {
  return {
    password: firstNonEmpty(config.password),
    apiKey: firstNonEmpty(config.apiKey, config.api_key),
    connectionString: firstNonEmpty(config.connectionString, config.connection_string),
  };
}

export function vectorCredentialsFromGate0Data(gate0 = {}) {
  const nested = vectorCredentialsFromConfig(gate0?.vector_store || {});
  return {
    password: firstNonEmpty(nested.password, gate0.vector_password),
    apiKey: firstNonEmpty(nested.apiKey, gate0.vector_api_key),
    connectionString: firstNonEmpty(nested.connectionString, gate0.vector_connection_string),
  };
}

/** Alias: HEAD stored secrets on flat vector_* keys. */
export const vectorStoreCredentialsFromGate0Data = vectorCredentialsFromGate0Data;

/**
 * Persist vector connection on gate0_data.vector_store, including the secret,
 * the same way traces stores api_key on gate0_data.traces.
 */
export function mergeVectorFormIntoGate0Data(gate0 = {}, form, credentials = {}) {
  const provider = normalizeVectorStoreProvider(form?.provider);
  if (!provider) return { ...gate0 };
  const previous = gate0.vector_store && typeof gate0.vector_store === "object" ? gate0.vector_store : {};
  const vector_store = {
    provider,
    host: String(form.host || "").trim() || undefined,
    port: String(form.port || "").trim() || undefined,
    database: String(form.database || "").trim() || undefined,
    username: String(form.username || "").trim() || undefined,
    table: String(form.table || "").trim() || undefined,
    vector_column: String(form.vectorColumn || "").trim() || undefined,
    index_name: String(form.indexName || "").trim() || undefined,
    namespace: String(form.namespace || "").trim() || undefined,
    collection: String(form.collection || "").trim() || undefined,
    vector_path: String(form.vectorPath || "").trim() || undefined,
    url: String(form.url || "").trim() || undefined,
    num_candidates: String(form.numCandidates || "").trim() || undefined,
  };
  const password = firstNonEmpty(credentials.password, previous.password);
  const apiKey = firstNonEmpty(credentials.apiKey, previous.api_key, previous.apiKey);
  const connectionString = firstNonEmpty(
    credentials.connectionString,
    previous.connection_string,
    previous.connectionString
  );
  if (password) vector_store.password = password;
  if (apiKey) vector_store.api_key = apiKey;
  if (connectionString) vector_store.connection_string = connectionString;
  return { ...gate0, vector_store };
}

/**
 * Remember the selected vector provider on gate0_data.vector_store even when the
 * rest of the connection is incomplete. Existing vector_store fields, including
 * secrets, are left in place. An empty selection does not clear a saved provider.
 */
export function mergeVectorProviderSelectionIntoGate0Data(gate0 = {}, form = {}) {
  const provider = normalizeVectorStoreProvider(form?.provider);
  if (!provider) return gate0;
  const previous =
    gate0.vector_store && typeof gate0.vector_store === "object" ? gate0.vector_store : {};
  if (normalizeVectorStoreProvider(previous.provider) === provider) return gate0;
  return {
    ...gate0,
    vector_store: {
      ...previous,
      provider,
    },
  };
}

/**
 * Optional merge of saved vector connection fields from gate0_data.
 * Prefers vector_store, then the Gate 1 summary, then legacy vector_* keys.
 * Does not copy SQL db_* fields onto the vector form.
 */
export function vectorStoreFormFromGate0Data(gate0 = {}) {
  const stored = gate0.vector_store && typeof gate0.vector_store === "object" ? gate0.vector_store : {};
  const summary =
    gate0.gate1_vector_store_readiness_summary &&
    typeof gate0.gate1_vector_store_readiness_summary === "object"
      ? gate0.gate1_vector_store_readiness_summary
      : {};
  const provider = normalizeVectorStoreProvider(
    stored.provider || summary.provider || gate0.vector_provider || gate0.vectorProvider
  );
  return withOperationDefaults({
    provider,
    host: firstNonEmpty(stored.host, summary.host, gate0.vector_host),
    port: firstNonEmpty(stored.port, summary.port, gate0.vector_port),
    database: firstNonEmpty(
      stored.database,
      summary.database,
      gate0.vector_database,
      gate0.vector_db_name
    ),
    username: firstNonEmpty(stored.username, summary.username, gate0.vector_username),
    table: firstNonEmpty(stored.table, summary.table, gate0.vector_table) || "documents",
    vectorColumn:
      firstNonEmpty(
        stored.vector_column,
        stored.vectorColumn,
        summary.vectorColumn,
        gate0.vector_column
      ) || "embedding",
    dimension: firstNonEmpty(summary.dimension, gate0.vector_dimension) || "3",
    queryVector: firstNonEmpty(gate0.vector_query_vector) || "[0.9, 0.1, 0]",
    topK: firstNonEmpty(stored.top_k, stored.topK, summary.topK, gate0.vector_top_k) || "5",
    indexName: firstNonEmpty(
      stored.index_name,
      stored.indexName,
      summary.indexName,
      gate0.vector_index_name,
      gate0.vector_index
    ),
    namespace: firstNonEmpty(stored.namespace, summary.namespace, gate0.vector_namespace),
    recordId: firstNonEmpty(gate0.vector_record_id),
    collection: firstNonEmpty(stored.collection, summary.collection, gate0.vector_collection),
    vectorPath: firstNonEmpty(
      stored.vector_path,
      stored.vectorPath,
      summary.vectorPath,
      gate0.vector_path
    ),
    url: firstNonEmpty(stored.url, summary.url, gate0.vector_url, gate0.vector_endpoint),
    numCandidates:
      firstNonEmpty(
        stored.num_candidates,
        stored.numCandidates,
        summary.numCandidates,
        gate0.vector_num_candidates
      ) || "100",
  });
}

function buildPgvectorPayload(form, credentials = {}) {
  const host = String(form.host || "").trim();
  const database = String(form.database || "").trim();
  const username = String(form.username || "").trim();
  const password = String(credentials.password ?? "");
  const table = String(form.table || "").trim();
  const port = parseInt(String(form.port || "").trim(), 10);

  if (!host) return { error: "Vector store host is required." };
  if (!database) return { error: "Vector store database is required." };
  if (!username) return { error: "Vector store username is required." };
  if (!password) {
    return { error: "Vector store password is required for the live test (not stored)." };
  }
  if (!Number.isInteger(port) || port <= 0) {
    return { error: "Vector store port must be a positive integer." };
  }
  if (!SQL_IDENT_RE.test(table)) {
    return { error: "Table must be a simple SQL identifier." };
  }

  return {
    vector_store_config: {
      provider: "pgvector",
      host,
      port,
      database,
      username,
      password,
      table,
    },
  };
}

/**
 * Pinecone readiness describes the index and its namespace rather than
 * fetching a named record: the check should not require the user to know a
 * vector id. A record_id on the form still selects the stricter fetch_record
 * check, which the backend continues to support.
 */
function buildPineconePayload(form, credentials = {}) {
  const indexName = String(form.indexName || "").trim();
  const namespace = String(form.namespace || "").trim();
  const recordId = String(form.recordId || "").trim();
  const apiKey = String(credentials.apiKey ?? credentials.api_key ?? "");

  if (!indexName) return { error: "Pinecone index name is required." };
  if (!apiKey) {
    return { error: "Pinecone API key is required for the live test (not stored)." };
  }

  return {
    vector_store_config: {
      provider: "pinecone",
      operation: recordId ? "fetch_record" : "describe_index",
      index_name: indexName,
      ...(namespace ? { namespace } : {}),
      ...(recordId ? { record_id: recordId } : {}),
      api_key: apiKey,
    },
  };
}

function buildMongodbPayload(form, credentials = {}) {
  const connectionString = String(
    credentials.connectionString ?? credentials.connection_string ?? ""
  );
  const database = String(form.database || "").trim();
  const collection = String(form.collection || "").trim();
  const indexName = String(form.indexName || "").trim();
  const vectorPath = String(form.vectorPath || "").trim();

  if (!connectionString) {
    return {
      error: "MongoDB connection string is required for the live test (not stored).",
    };
  }
  if (!database) return { error: "MongoDB database is required." };
  if (!collection) return { error: "MongoDB collection is required." };
  if (!indexName) return { error: "MongoDB index name is required." };
  if (!vectorPath) return { error: "MongoDB vector path is required." };

  return {
    vector_store_config: {
      provider: "mongodb",
      connection_string: connectionString,
      database,
      collection,
      index_name: indexName,
      vector_path: vectorPath,
    },
  };
}

function buildWeaviatePayload(form, credentials = {}) {
  const url = String(form.url || form.endpoint || "").trim();
  const collection = String(form.collection || "").trim();
  const apiKey = String(credentials.apiKey ?? credentials.api_key ?? "");

  if (!url) return { error: "Weaviate URL / Endpoint is required." };
  if (!apiKey) {
    return { error: "Weaviate API key is required for the live test (not stored)." };
  }
  if (!collection) return { error: "Weaviate collection is required." };

  return {
    vector_store_config: {
      provider: "weaviate",
      url,
      collection,
      api_key: apiKey,
    },
  };
}

export function buildVectorStoreConfigPayload(form, credentials = {}) {
  const rawProvider = String(form?.provider || "").trim();
  if (!rawProvider) {
    return { error: "Vector store provider is required." };
  }
  const provider = normalizeVectorStoreProvider(rawProvider);
  if (!provider) {
    return { error: `Unsupported vector store provider: ${form.provider}` };
  }
  if (provider === "pgvector") {
    return buildPgvectorPayload(form, credentials);
  }
  if (provider === "pinecone") {
    return buildPineconePayload(form, credentials);
  }
  if (provider === "mongodb") {
    return buildMongodbPayload(form, credentials);
  }
  if (provider === "weaviate") {
    return buildWeaviatePayload(form, credentials);
  }
  return { error: `Unsupported vector store provider: ${form.provider}` };
}

export function vectorStoreFormCanTest(form, credentials = {}) {
  const provider = normalizeVectorStoreProvider(form?.provider);
  if (!provider || isVectorStoreProviderUnavailable(provider)) return false;
  const fields = fieldsForProvider(provider);
  return fields.every((field) => {
    if (!field.required) return true;
    const value = field.credential
      ? credentials[field.key]
      : form[field.key];
    return Boolean(String(value ?? "").trim());
  });
}

/** Provider, non-secret fields, and the secret — required before Gate 2 can reuse this connection. */
export function vectorStoreFormCanSave(form, credentials = {}) {
  return vectorStoreFormCanTest(form, credentials);
}

export function buildVectorStoreSaveSummary(form, previous = null) {
  const provider = normalizeVectorStoreProvider(form?.provider);
  const keepReady =
    previous &&
    normalizeVectorStoreProvider(previous.provider) === provider &&
    String(previous.overall_status || "").toLowerCase() === "ready";
  return {
    ...(previous && typeof previous === "object" ? previous : {}),
    overall_status: keepReady ? "ready" : "saved",
    generated_at: keepReady ? previous.generated_at : new Date().toISOString(),
    provider,
    host: String(form.host || "").trim() || null,
    port: String(form.port || "").trim() || null,
    database: String(form.database || "").trim() || null,
    username: String(form.username || "").trim() || null,
    table: String(form.table || "").trim() || null,
    vectorColumn: String(form.vectorColumn || "").trim() || null,
    indexName: String(form.indexName || "").trim() || null,
    namespace: String(form.namespace || "").trim() || null,
    collection: String(form.collection || "").trim() || null,
    vectorPath: String(form.vectorPath || "").trim() || null,
    numCandidates: String(form.numCandidates || "").trim() || null,
    dimension: String(form.dimension || "").trim() || null,
  };
}

const VECTOR_GATE0_KEYS = [
  "gate1_vector_store_readiness_summary",
  "vector_store",
  "vector_provider",
  "vectorProvider",
  "vector_host",
  "vector_port",
  "vector_database",
  "vector_db_name",
  "vector_username",
  "vector_table",
  "vector_column",
  "vector_dimension",
  "vector_query_vector",
  "vector_top_k",
  "vector_index_name",
  "vector_index",
  "vector_namespace",
  "vector_record_id",
  "vector_collection",
  "vector_path",
  "vector_num_candidates",
  "vector_api_key",
  "vector_password",
  "vector_connection_string",
];

/** Drop the saved vector store so Gate 2 no longer treats it as declared. */
export function clearVectorStoreFromGate0Data(gate0 = {}) {
  const next = { ...gate0 };
  VECTOR_GATE0_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

function pickSecret(nextValue, existingValue) {
  const next = String(nextValue ?? "").trim();
  if (next) return next;
  const existing = String(existingValue ?? "").trim();
  return existing || undefined;
}

/**
 * Persist vector connection on legacy flat vector_* keys (HEAD shape).
 * Incoming Gate 2 reuse uses mergeVectorFormIntoGate0Data (nested vector_store).
 */
export function mergeVectorStoreFormIntoGate0Data(gate0 = {}, vectorForm = {}, credentials = {}) {
  const provider = normalizeVectorStoreProvider(vectorForm.provider);
  return {
    ...gate0,
    vector_provider: provider || undefined,
    vector_host: String(vectorForm.host || "").trim() || undefined,
    vector_port: String(vectorForm.port || "").trim() || undefined,
    vector_database: String(vectorForm.database || "").trim() || undefined,
    vector_username: String(vectorForm.username || "").trim() || undefined,
    vector_table: String(vectorForm.table || "").trim() || undefined,
    vector_column: String(vectorForm.vectorColumn || "").trim() || undefined,
    vector_dimension: String(vectorForm.dimension || "").trim() || undefined,
    vector_query_vector: String(vectorForm.queryVector || "").trim() || undefined,
    vector_top_k: String(vectorForm.topK || "").trim() || undefined,
    vector_index_name: String(vectorForm.indexName || "").trim() || undefined,
    vector_namespace: String(vectorForm.namespace || "").trim() || undefined,
    vector_record_id: String(vectorForm.recordId || "").trim() || undefined,
    vector_collection: String(vectorForm.collection || "").trim() || undefined,
    vector_path: String(vectorForm.vectorPath || "").trim() || undefined,
    vector_num_candidates: String(vectorForm.numCandidates || "").trim() || undefined,
    vector_api_key: pickSecret(credentials.apiKey, gate0.vector_api_key),
    vector_password: pickSecret(credentials.password, gate0.vector_password),
    vector_connection_string: pickSecret(
      credentials.connectionString,
      gate0.vector_connection_string
    ),
  };
}

function sameConfigValue(left, right) {
  return String(left ?? "").trim() === String(right ?? "").trim();
}

/**
 * Non-secret identity of the current vector-store form.
 * Credential edits do not change this fingerprint.
 */
export function vectorStoreConfigFingerprint(form = {}) {
  const provider = normalizeVectorStoreProvider(form.provider);
  const keys = fieldsForProvider(provider)
    .filter((field) => !field.credential)
    .map((field) => field.key);
  return [provider, ...keys.map((key) => String(form[key] ?? "").trim())].join("\u0001");
}

export function tagVectorReportWithForm(report, form) {
  if (!report || typeof report !== "object") return report;
  return {
    ...report,
    config_fingerprint: vectorStoreConfigFingerprint(form),
  };
}

export function vectorReportMatchesForm(report, form) {
  if (!report || !form) return false;
  const reportProvider = normalizeVectorStoreProvider(report.provider);
  const formProvider = normalizeVectorStoreProvider(form.provider);
  if (!reportProvider) return false;
  if (!formProvider) return true;
  if (reportProvider !== formProvider) return false;
  if (report.config_fingerprint) {
    return report.config_fingerprint === vectorStoreConfigFingerprint(form);
  }
  if (formProvider === "pgvector") {
    return ["host", "port", "database", "table"].every((key) => {
      const formVal = String(form[key] ?? "").trim();
      if (!formVal) return true;
      return sameConfigValue(formVal, report[key]);
    });
  }
  if (formProvider === "pinecone") {
    return ["indexName", "namespace"].every((key) => {
      const formVal = String(form[key] ?? "").trim();
      if (!formVal) return true;
      const reportVal = key === "indexName" ? report.indexName || report.index_name : report[key];
      return sameConfigValue(formVal, reportVal);
    });
  }
  if (formProvider === "mongodb") {
    return ["database", "collection", "indexName", "vectorPath"].every((key) => {
      const formVal = String(form[key] ?? "").trim();
      if (!formVal) return true;
      const reportVal =
        key === "indexName"
          ? report.indexName || report.index_name
          : key === "vectorPath"
            ? report.vectorPath || report.vector_path
            : report[key];
      return sameConfigValue(formVal, reportVal);
    });
  }
  if (formProvider === "weaviate") {
    return ["url", "collection"].every((key) => {
      const formVal = String(form[key] ?? "").trim();
      if (!formVal) return true;
      const reportVal = key === "url" ? report.url || report.endpoint : report[key];
      return sameConfigValue(formVal, reportVal);
    });
  }
  return false;
}

/**
 * PostgreSQL reachability inferred from a pgvector connection stage.
 * Pinecone and MongoDB reports never contribute SQL database evidence.
 */
export function postgresReachabilityFromVectorReport(report) {
  if (!report || typeof report !== "object") return null;
  if (normalizeVectorStoreProvider(report.provider) !== "pgvector") return null;
  if (String(report.connection || "").toLowerCase() !== "passed") return null;
  return {
    connected: true,
    db_type: "postgresql",
    host: report.host || null,
    port: report.port ?? null,
    database: report.database || null,
    latency_ms: report.latencyMs ?? report.latency_ms ?? null,
    source: "pgvector",
  };
}
